/**
 * Status: the platform's public status page as its administrators see it. What anyone sees at the
 * API's /status (each component as last checked, the open incidents and the last 30 days' resolved
 * ones), and the incidents themselves: open one, add to its timeline as things change, resolve it.
 * On Mojo Up AI Cloud only Mojo Up writes them; an organisation's administrators read the page.
 *
 * The data column (Layout.tsx): components, open incidents and resolved ones as list groups, each
 * row with its state as a mark and one menu; opening, updating and resolving are sheets.
 */

import { ArrowUpRightFromSquare, CircleCheck, Clock, HeartPulse, Megaphone, Server } from '@gravity-ui/icons';
import { Button, Input, Label, TextArea, TextField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { STATUS_COMPONENT_NAMES, STATUS_IMPACT_WORDS, type StatusComponentId, type StatusImpact, type StatusIncident, type StatusState } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';

import { problemText, statusRoutes } from '../api/client.ts';
import { Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark, Tick } from '../components/ui.tsx';
import type { ArtTone } from '../components/ui/Art.tsx';
import { FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone: ArtTone = 'slate';

const MARK: Record<StatusState, { state: 'online' | 'medium' | 'error'; label: string }> = {
  ok: { state: 'online', label: 'Working' },
  degraded: { state: 'medium', label: 'Degraded' },
  down: { state: 'error', label: 'Down' },
};

/** An open incident's impact as a mark: how bad, in a word and a dot. */
const IMPACT_MARK: Record<StatusImpact, 'medium' | 'high' | 'error'> = { minor: 'medium', major: 'high', outage: 'error' };

const IMPACT_HELP: Record<StatusImpact, string> = {
  minor: 'Some people notice: slower, or one feature misbehaving.',
  major: 'A part of the platform does not work for many people.',
  outage: 'The platform does not work. The page shows a major outage.',
};

const when = (iso: string) => dateTime(iso, { date: 'medium', year: false });

type Editing = { kind: 'open' } | { kind: 'update'; incident: StatusIncident } | { kind: 'resolve'; incident: StatusIncident };

export function StatusPage() {
  const summary = useLoad(() => statusRoutes.summary(), [], 30_000);
  const list = useLoad(() => statusRoutes.incidents(), []);
  const [editing, setEditing] = useState<Editing>();
  const [error, setError] = useState<string>();
  const s = summary.data;
  const mayWrite = list.data?.mayWrite ?? false;
  const affected = (s?.components ?? []).filter((c) => c.state !== 'ok').length;
  const open = (list.data?.incidents ?? []).filter((i) => !i.resolvedAt);
  // The last 30 days' resolved incidents, as the public page lists them.
  const resolved = s?.incidents.resolved ?? [];
  const done = () => {
    setEditing(undefined);
    list.reload();
    summary.reload();
  };
  const reopen = async (i: StatusIncident) => {
    setError(undefined);
    try {
      await statusRoutes.change(i.id, { resolvedAt: null });
      done();
    } catch (e) {
      setError(problemText(e));
    }
  };
  const openButton = (
    <Button size="sm" variant="primary" onPress={() => setEditing({ kind: 'open' })}>
      Open an incident
    </Button>
  );

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<HeartPulse />}
        title="Status"
        subtitle="What anyone sees on this platform’s public status page, and the incidents you write for it."
        actions={
          <>
            <Button size="sm" variant="secondary" onPress={() => window.open(statusRoutes.pageUrl, '_blank', 'noopener')}>
              <ArrowUpRightFromSquare className="size-4" />
              Public page
            </Button>
            {mayWrite ? openButton : null}
          </>
        }
      />
      <Explainer id="status" tone={tone} icon={<HeartPulse />} title="How the status page works" summary="Components are checked every 30 seconds; incidents are written by people">
        <p>The platform checks each of its parts every 30 seconds and the public page shows each one as working, degraded or down. Anyone can open that page without signing in.</p>
        <p>An incident is what a person writes when something is wrong: what it touches, how bad it is, and an update each time something changes. It shows on the public page at once, and stays there for 30 days after it is resolved.</p>
      </Explainer>

      <PartBoundary key="status" name="Status" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<HeartPulse />} label="Platform" value={s ? MARK[s.overall].label : undefined} detail={s ? (affected ? `${affected} affected` : 'All working') : ''} />
          <Stat tone={tone} icon={<Megaphone />} label="Open incidents" value={list.data ? open.length : undefined} detail={open.length ? `Oldest ${timeAgo(open[open.length - 1]!.startedAt)}` : 'Nothing open'} needsYou={open.length > 0} />
          <Stat tone={tone} icon={<Clock />} label="Last checked" value={s?.checkedAt ? timeAgo(s.checkedAt) : undefined} detail="Every 30 seconds" />
        </div>

        <ErrorNote error={error ?? summary.error ?? list.error} />
        {list.data && !mayWrite && list.data.why ? <p className="text-sm text-muted">{list.data.why}</p> : null}

        <ListGroup
          title="Components"
          count={s?.components.length || undefined}
          description="As the public page shows them."
          empty={s && s.components.length === 0 ? <Empty tone={tone} icon={<Server />} title="Not checked yet" body="The first check runs within half a minute of the platform starting." /> : undefined}
        >
          {!s ? <ListBlock className="text-sm text-muted">Reading the last check…</ListBlock> : null}
          {(s?.components ?? []).map((c) => (
            <ListRow key={c.id} title={c.name} description={c.message} status={<StatusMark state={MARK[c.state].state} label={MARK[c.state].label} />} />
          ))}
        </ListGroup>

        <ListGroup
          title="Open incidents"
          count={open.length || undefined}
          description="Shown on the public page until resolved. Add an update each time something changes."
          empty={
            list.data && open.length === 0 ? (
              <Empty tone={tone} icon={<CircleCheck />} title="No open incidents" body="When something is wrong, open an incident so people see what is happening and when it changes." action={mayWrite ? openButton : undefined} />
            ) : undefined
          }
        >
          {open.map((i) => (
            <IncidentRow
              key={i.id}
              incident={i}
              menu={mayWrite ? [{ id: 'update', label: 'Add an update' }, { id: 'resolve', label: 'Resolve' }] : undefined}
              onAction={(id) => setEditing({ kind: id === 'resolve' ? 'resolve' : 'update', incident: i })}
            />
          ))}
        </ListGroup>

        <ListGroup
          title="Resolved"
          count={resolved.length || undefined}
          description="The last 30 days, as the public page lists them."
          empty={s && resolved.length === 0 ? <Empty tone={tone} icon={<CircleCheck />} title="Nothing resolved lately" body="Incidents resolved in the last 30 days show here and on the public page." /> : undefined}
        >
          {resolved.map((i) => (
            <IncidentRow key={i.id} incident={i} menu={mayWrite ? [{ id: 'reopen', label: 'Reopen' }] : undefined} onAction={() => void reopen(i)} />
          ))}
        </ListGroup>
      </PartBoundary>
      {editing?.kind === 'open' && <OpenIncident onClose={() => setEditing(undefined)} onDone={done} />}
      {editing && editing.kind !== 'open' && <ChangeIncident incident={editing.incident} resolving={editing.kind === 'resolve'} onClose={() => setEditing(undefined)} onDone={done} />}
    </PageColumn>
  );
}

function IncidentRow({ incident: i, menu, onAction }: { incident: StatusIncident; menu?: Array<{ id: string; label: string }>; onAction: (id: string) => void }) {
  const latest = i.updates[i.updates.length - 1];
  const names = i.components.map((c) => STATUS_COMPONENT_NAMES[c]).join(', ');
  return (
    <ListRow
      title={i.title}
      description={
        <>
          <span className="block">
            Began {when(i.startedAt)}
            {i.resolvedAt ? ` · resolved ${when(i.resolvedAt)}` : ''}
            {names ? ` · ${names}` : ''}
          </span>
          {latest ? (
            <span className="mt-1 block whitespace-pre-wrap text-foreground">
              {latest.text} <span className="text-muted">({timeAgo(latest.at)})</span>
            </span>
          ) : null}
        </>
      }
      status={i.resolvedAt ? <StatusMark state="online" label="Resolved" /> : <StatusMark state={IMPACT_MARK[i.impact]} label={STATUS_IMPACT_WORDS[i.impact]} />}
      compactControl
      control={menu ? <RowMenu label={`More for ${i.title}`} items={menu} onAction={onAction} /> : undefined}
    />
  );
}

function ImpactField({ value, onChange }: { value: StatusImpact; onChange: (v: StatusImpact) => void }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-xs font-medium text-foreground">Impact</span>
      <Segment aria-label="Impact" size="sm" selectedKey={value} onSelectionChange={(k) => onChange(String(k) as StatusImpact)} className="w-fit">
        {(Object.keys(STATUS_IMPACT_WORDS) as StatusImpact[]).map((k) => (
          <Segment.Item key={k} id={k}>
            {STATUS_IMPACT_WORDS[k]}
          </Segment.Item>
        ))}
      </Segment>
      <p className="text-xs text-muted">{IMPACT_HELP[value]}</p>
    </div>
  );
}

function Words({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <TextField className="w-full" variant="secondary" value={value} onChange={onChange} isRequired>
      <Label>{label}</Label>
      <TextArea className="w-full resize-y" rows={4} maxLength={2000} placeholder={placeholder} />
    </TextField>
  );
}

/** The sheet's foot: Cancel, then the one primary. */
function Foot({ label, busy, canSubmit, onClose, onSubmit }: { label: string; busy: boolean; canSubmit: boolean; onClose: () => void; onSubmit: () => void }) {
  return (
    <>
      <Button size="sm" variant="tertiary" isDisabled={busy} onPress={onClose}>
        Cancel
      </Button>
      <Button size="sm" variant="primary" isPending={busy} isDisabled={!canSubmit} onPress={onSubmit}>
        {label}
      </Button>
    </>
  );
}

function OpenIncident({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [impact, setImpact] = useState<StatusImpact>('minor');
  const [components, setComponents] = useState<StatusComponentId[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await statusRoutes.open({ title: title.trim(), impact, components, text: text.trim() });
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet title="Open an incident" description="It shows on the public status page at once. Write for the people using the platform." onClose={onClose} footer={<Foot label="Open incident" busy={busy} canSubmit={!!title.trim() && !!text.trim()} onClose={onClose} onSubmit={() => void submit()} />}>
      <TextField variant="secondary" value={title} onChange={setTitle} isRequired autoFocus>
        <Label>What is wrong</Label>
        <Input placeholder="Remote control is slow to connect" maxLength={200} />
      </TextField>
      <ImpactField value={impact} onChange={setImpact} />
      <fieldset className="grid gap-1.5">
        <legend className="mb-1.5 text-xs font-medium text-foreground">What it touches</legend>
        <span className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {(Object.keys(STATUS_COMPONENT_NAMES) as StatusComponentId[]).map((c) => (
            <Tick key={c} checked={components.includes(c)} onChange={(on) => setComponents(on ? [...components, c] : components.filter((x) => x !== c))}>
              {STATUS_COMPONENT_NAMES[c]}
            </Tick>
          ))}
        </span>
      </fieldset>
      <Words label="First update" value={text} onChange={setText} placeholder="We are looking into reports that…" />
      <ErrorNote error={error} />
    </FormSheet>
  );
}

function ChangeIncident({ incident, resolving, onClose, onDone }: { incident: StatusIncident; resolving: boolean; onClose: () => void; onDone: () => void }) {
  const [impact, setImpact] = useState<StatusImpact>(incident.impact);
  const [text, setText] = useState(resolving ? 'This is resolved. Thank you for your patience.' : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await statusRoutes.change(incident.id, { update: { text: text.trim() }, ...(resolving ? { resolvedAt: new Date().toISOString() } : impact !== incident.impact ? { impact } : {}) });
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet title={resolving ? 'Resolve the incident' : 'Add an update'} description={incident.title} onClose={onClose} footer={<Foot label={resolving ? 'Resolve' : 'Post update'} busy={busy} canSubmit={!!text.trim()} onClose={onClose} onSubmit={() => void submit()} />}>
      {!resolving && <ImpactField value={impact} onChange={setImpact} />}
      <Words label={resolving ? 'Last word' : 'Update'} value={text} onChange={setText} placeholder="What changed, and what happens next" />
      <ErrorNote error={error} />
    </FormSheet>
  );
}
